/**
 * Cyber panel — Feodo/URLhaus/OTX cyber threat feed.
 *
 * Filters events where source === 'cyber'. Category tabs: All, C2 Servers,
 * Malware URLs, Threat Intel. Color-coded by severity.
 * Click → emit event:selected for detail view.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { relativeTime } from '@/utils/time';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import type { NormalizedEvent, Severity } from '@/sources/base-source';

type CyberCategory = 'all' | 'c2' | 'malware' | 'threat-intel';

const CATEGORY_TABS: { label: string; value: CyberCategory }[] = [
  { label: 'All', value: 'all' },
  { label: 'C2 Servers', value: 'c2' },
  { label: 'Malware URLs', value: 'malware' },
  { label: 'Threat Intel', value: 'threat-intel' },
];

export function createCyberPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'cyber-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const tabBar = document.createElement('div');
  tabBar.style.cssText = `
    display: flex; gap: 4px; padding: 8px 12px; flex-wrap: wrap;
    border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  `;
  container.appendChild(tabBar);

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px; flex: 1; overflow-y: auto;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;
  let activeTab: CyberCategory = 'all';

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function renderTabs(): void {
    tabBar.textContent = '';
    for (const tab of CATEGORY_TABS) {
      const btn = document.createElement('button');
      btn.style.cssText = `
        padding: 4px 10px; border-radius: 12px; border: 1px solid var(--border-primary);
        font-size: 11px; cursor: pointer; transition: all 150ms;
        background: ${activeTab === tab.value ? 'var(--accent)' : 'var(--bg-secondary)'};
        color: ${activeTab === tab.value ? '#fff' : 'var(--text-secondary)'};
      `;
      btn.textContent = tab.label;
      btn.onclick = () => {
        activeTab = tab.value;
        renderTabs();
        scheduleRender();
      };
      tabBar.appendChild(btn);
    }
  }

  function inferCyberCategory(ev: NormalizedEvent): CyberCategory {
    const tags = ev.tags ?? [];
    for (const tag of tags) {
      if (tag === 'c2' || tag === 'botnet' || tag === 'command-and-control') return 'c2';
      if (tag === 'malware' || tag === 'urlhaus' || tag === 'phishing') return 'malware';
      if (tag === 'threat-intel' || tag === 'otx' || tag === 'ioc') return 'threat-intel';
    }
    const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();
    if (text.includes('c2') || text.includes('c&c') || text.includes('botnet') || text.includes('command and control')) return 'c2';
    if (text.includes('malware') || text.includes('urlhaus') || text.includes('phishing') || text.includes('trojan')) return 'malware';
    return 'threat-intel';
  }

  function render(): void {
    const events = appStore.getState().events;
    let cyberEvents = events
      .filter(e => e.source === 'cyber')
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (activeTab !== 'all') {
      cyberEvents = cyberEvents.filter(e => inferCyberCategory(e) === activeTab);
    }

    contentArea.textContent = '';

    // Summary header with breakdown
    const summaryHeader = document.createElement('div');
    summaryHeader.style.cssText = `
      margin-bottom: 12px; padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const totalRow = document.createElement('div');
    totalRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 6px;';

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${cyberEvents.length > 0 ? 'var(--severity-critical)' : 'var(--text-muted)'};`;
    countNum.textContent = String(cyberEvents.length);
    totalRow.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `active threat${cyberEvents.length !== 1 ? 's' : ''}`;
    totalRow.appendChild(countLabel);

    summaryHeader.appendChild(totalRow);

    // Breakdown by category (only in 'all' tab)
    if (activeTab === 'all' && cyberEvents.length > 0) {
      const c2Count = cyberEvents.filter(e => inferCyberCategory(e) === 'c2').length;
      const malwareCount = cyberEvents.filter(e => inferCyberCategory(e) === 'malware').length;
      const intelCount = cyberEvents.filter(e => inferCyberCategory(e) === 'threat-intel').length;

      const breakdownRow = document.createElement('div');
      breakdownRow.style.cssText = 'display: flex; gap: 8px; flex-wrap: wrap;';

      for (const [label, count] of [['C2', c2Count], ['Malware', malwareCount], ['Intel', intelCount]] as const) {
        const chip = document.createElement('span');
        chip.style.cssText = `
          font-size: 10px; padding: 2px 6px; border-radius: 3px;
          background: var(--bg-primary); color: var(--text-secondary);
        `;
        chip.textContent = `${label}: ${count}`;
        breakdownRow.appendChild(chip);
      }

      summaryHeader.appendChild(breakdownRow);
    }

    contentArea.appendChild(summaryHeader);

    if (cyberEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No cyber threats detected';
      contentArea.appendChild(msg);
      return;
    }

    for (const ev of cyberEvents.slice(0, 80)) {
      const sev = ev.severity ?? 'medium';
      const row = document.createElement('div');
      row.style.cssText = `
        padding: 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary);
        border-left: 3px solid ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
        cursor: pointer;
      `;

      row.onclick = () => eventBus.emit('event:selected', ev);
      row.title = 'Click for details';

      // Title row
      const titleRow = document.createElement('div');
      titleRow.style.cssText = 'display: flex; align-items: center; gap: 6px;';

      // Threat type badge
      const catBadge = document.createElement('span');
      const cat = inferCyberCategory(ev);
      catBadge.style.cssText = `
        font-size: 9px; font-weight: 600; padding: 1px 5px; border-radius: 3px;
        background: var(--bg-primary); color: var(--text-muted);
        text-transform: uppercase;
      `;
      catBadge.textContent = cat === 'c2' ? 'C2' : cat === 'malware' ? 'MAL' : 'INTEL';
      titleRow.appendChild(catBadge);

      // IP/domain from title
      const titleEl = document.createElement('span');
      titleEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;';
      titleEl.textContent = ev.title;
      titleRow.appendChild(titleEl);

      // Severity badge
      const sevBadge = document.createElement('span');
      sevBadge.style.cssText = `
        font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 3px;
        text-transform: uppercase;
        background: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'}22;
        color: ${SEVERITY_CSS_COLORS[sev as Severity] ?? 'var(--text-muted)'};
      `;
      sevBadge.textContent = sev;
      titleRow.appendChild(sevBadge);

      row.appendChild(titleRow);

      // Country attribution row
      const metaRow = document.createElement('div');
      metaRow.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-top: 3px;';

      if (ev.location?.country || ev.location?.countryCode) {
        const locEl = document.createElement('span');
        locEl.style.cssText = 'font-size: 10px; color: var(--text-secondary);';
        locEl.textContent = ev.location.country ?? ev.location.countryCode ?? '';
        metaRow.appendChild(locEl);
      }

      const timeEl = document.createElement('span');
      timeEl.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-left: auto;';
      timeEl.textContent = relativeTime(ev.timestamp);
      metaRow.appendChild(timeEl);

      row.appendChild(metaRow);

      contentArea.appendChild(row);
    }
  }

  renderTabs();
  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
